import '../css/app.css';

import { removeAiStandIns } from '@/composables/ai-upload/useAiPaintRegions';
import { rememberLeadTestFlag } from '@/composables/useLeadTracking';
import { registerWishlistHistoryReload } from '@/composables/useWishlist';
import { ssrViewportWidthPx } from '@/constants/viewport';
import safeHref from '@/directives/SafeHref';
import { safeHtml } from '@/directives/SafeHtml';
import i18n, { loadLocaleMessages } from '@/i18n';
import { registerMissingCartRedirect } from '@/lib/checkout/missingCart';
import { registerDeferredPropsLoopGuard } from '@/lib/deferredPropsLoopGuard';
import { registerLeadAttributionCapture } from '@/lib/leadAttribution';
import { registerPageViewTracking } from '@/lib/pageView';
import { setupStoreLocale } from '@/lib/storeSetup';
import { registerTrustbadge } from '@/lib/trustedShops';
import { registerXsrfCookieGuard } from '@/lib/xsrfCookieGuard';
import { useMobileSearchStore } from '@/stores/menu/MobileSearchStore';
import type { StoreObject, TrustedShopsConfigObject } from '@/types/generated';
import { getNormalizedInitialPage, readInitialPage } from '@/utils/initialPage';
import { restoreScrollPosition } from '@/utils/restoreScrollPosition';
import { loadThemeCss } from '@/utils/themeCss';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { configureEcho } from '@laravel/echo-vue';
import { provideSSRWidth } from '@vueuse/core';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import type { DefineComponent } from 'vue';
import { createApp, createSSRApp, h } from 'vue';

configureEcho({
    broadcaster: 'reverb',
});

const FALLBACK_LOCALE = 'en_EN';
const DEFAULT_LOCALE = 'nl_NL';

function getInitialLocale(): string {
    const store = readInitialPage()?.props?.store as StoreObject | undefined;

    return store?.language_locale ?? DEFAULT_LOCALE;
}

// Load only the active store's locale and English fallback in the initial bundle, lazy-load other locales on demand
await Promise.all([loadLocaleMessages(getInitialLocale()), loadLocaleMessages(FALLBACK_LOCALE)]);

function releaseScrollLock(): void {
    if (typeof document === 'undefined') {
        return;
    }

    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.classList.remove('overflow-hidden');
}

// Registered before the app boots, so the first load's navigate event is caught too.
if (typeof window !== 'undefined') {
    registerXsrfCookieGuard();
    registerDeferredPropsLoopGuard();
    registerPageViewTracking();
    registerMissingCartRedirect();
    rememberLeadTestFlag();
    registerLeadAttributionCapture();
    registerWishlistHistoryReload();
    registerTrustbadge(readInitialPage()?.props?.trusted_shops as TrustedShopsConfigObject | null | undefined);
}

await createInertiaApp({
    page: getNormalizedInitialPage(),
    title: title => title ?? undefined,
    resolve: (name: string): Promise<DefineComponent> =>
        resolvePageComponent(`./pages/${name}.vue`, import.meta.glob<DefineComponent>(['./pages/**/*.vue', '!./pages/**/[Pp]artials/**'])),
    setup({ el, App, props, plugin }) {
        const pinia = createPinia();
        // Only use persistedstate plugin on the client-side
        if (typeof window !== 'undefined') {
            pinia.use(piniaPluginPersistedstate);
        }

        const store = props.initialPage.props.store as StoreObject | undefined;
        setupStoreLocale(store?.language_locale);

        // Use createSSRApp for hydration when SSR HTML exists, otherwise use createApp to avoid hydration warnings in dev.
        const hasSsrContent = !!el?.innerHTML?.trim();
        const app = (hasSsrContent ? createSSRApp : createApp)({ render: () => h(App, props) })
            .use(plugin)
            .use(pinia)
            .use(i18n)
            .directive('safe-href', safeHref)
            .directive('safe-html', safeHtml);

        provideSSRWidth(ssrViewportWidthPx, app);

        if (!el) {
            return app;
        }

        if (hasSsrContent) {
            removeAiStandIns();
        }

        app.mount(el);
        restoreScrollPosition();

        // Remove Inertia's initial page data from DOM after mount to prevent GTM from capturing all props (bloating dataLayer by 60k+ lines), since Inertia only reads it once during bootstrap.
        el?.removeAttribute('data-page');
        document.querySelector('script[data-page]')?.remove();

        if (store) {
            loadThemeCss(store.magento_store_code);
        }

        return app;
    },
    progress: {
        color: '#4B5563',
    },
});

if (typeof window !== 'undefined') {
    // Prevent unhandled-rejection noise from transient network failures (offline,
    // ad-blocker, cancelled request). Inertia v3 emits this instead of 'exception'.
    router.on('networkError', event => {
        event.preventDefault();
    });

    router.on('navigate', () => {
        releaseScrollLock();
        // Close mobile search overlay on navigation (client-side only via Pinia store, never during SSR).
        useMobileSearchStore().closeSearch();
    });
}
